import React from "react";
import { Link } from "react-router-dom";
import { LiveDemo } from "../components/LiveDemo";
import { Reveal } from "../components/Reveal";
import { SectionHead, CtaBand } from "../components/UI";
import {
  IconMic,
  IconText,
  IconBrain,
  IconDoc,
  IconTask,
  IconUsers,
  IconUpload,
  IconCheck,
  IconArrow,
  IconShield,
  IconClock,
  IconSearch,
  IconBolt,
  IconBuilding,
  IconBriefcase,
  IconLayers,
  IconFingerprint,
} from "../components/Icons";

export function Home() {
  return (
    <>
      {/* ───────────────────────── Hero ───────────────────────── */}
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <Reveal>
              <span className="chip">
                <span className="dot" /> Phiên âm tiếng Việt theo thời gian thực
              </span>
              <h1>
                Họp xong <span className="grad">là xong việc.</span>
              </h1>
              <p className="hero-sub">
                Ghi Âm Pro ghi âm cuộc họp, chuyển giọng nói thành văn bản theo từng người nói,
                rồi tự động sinh <b style={{ color: "#fff" }}>kết luận</b> và{" "}
                <b style={{ color: "#fff" }}>danh sách nhiệm vụ</b> — tất cả bằng AI.
              </p>
              <div className="hero-cta">
                <Link to="/lien-he" className="btn btn-primary btn-lg">
                  Yêu cầu demo <IconArrow size={18} />
                </Link>
                <Link to="/tinh-nang" className="btn btn-light btn-lg">
                  Khám phá tính năng
                </Link>
              </div>
              <div className="hero-meta">
                <span>
                  <b>Tiếng Việt</b> phân biệt người nói
                </span>
                <span>
                  <b>Biên bản & nhiệm vụ</b> tự động
                </span>
                <span>
                  <b>Xuất Word/PDF</b> theo mẫu
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <LiveDemo />
          </Reveal>
        </div>
      </section>

      {/* ───────────────────────── Trust ───────────────────────── */}
      <section className="section tight">
        <div className="container trust">
          <Reveal>
            <div className="label">Phù hợp với cơ quan, đơn vị và doanh nghiệp</div>
            <div className="logos">
              <span className="logo">
                <IconBuilding size={20} /> Cơ quan hành chính
              </span>
              <span className="logo">
                <IconUsers size={20} /> Văn phòng UBND
              </span>
              <span className="logo">
                <IconBriefcase size={20} /> Doanh nghiệp
              </span>
              <span className="logo">
                <IconLayers size={20} /> Ban quản lý dự án
              </span>
              <span className="logo">
                <IconShield size={20} /> Khối nội chính
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────────────────── Stats (dark) ───────────────────────── */}
      <section className="section dark">
        <div className="container">
          <div className="stats">
            {[
              ["90%", "thời gian soạn biên bản được cắt giảm"],
              ["60+", "ngôn ngữ hỗ trợ, tối ưu cho tiếng Việt"],
              ["100%", "nhiệm vụ được bóc tách kèm thời hạn"],
              ["1 phút", "từ kết thúc họp tới bản dự thảo đầu tiên"],
            ].map(([v, l], i) => (
              <Reveal key={i} className="stat" delay={i * 80}>
                <div className="v">{v}</div>
                <div className="l">{l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── Value props ───────────────────────── */}
      <section className="section">
        <div className="container">
          <SectionHead
            center
            eyebrow="Vì sao chọn Ghi Âm Pro"
            title="Không chỉ ghi âm — mà hoàn tất cả phần việc sau họp"
            sub="Bốn năng lực cốt lõi biến dữ liệu âm thanh thành thông tin có cấu trúc, sẵn sàng để trình ký và theo dõi."
          />
          <div className="grid cards-4">
            {[
              {
                ic: <IconText size={24} />,
                cls: "",
                h: "Phiên âm chính xác",
                p: "Chuyển giọng nói thành văn bản theo mốc thời gian, phân biệt từng người nói, tối ưu cho tiếng Việt.",
              },
              {
                ic: <IconBrain size={24} />,
                cls: "violet",
                h: "Tóm tắt bằng AI",
                p: "Tự động rút ý chính, chủ đề, dòng thời gian và các quyết định đã thống nhất trong cuộc họp.",
              },
              {
                ic: <IconTask size={24} />,
                cls: "teal",
                h: "Giao việc rõ ràng",
                p: "Bóc tách nhiệm vụ kèm đơn vị/cá nhân phụ trách và thời hạn để tiện theo dõi sau họp.",
              },
              {
                ic: <IconDoc size={24} />,
                cls: "amber",
                h: "Biên bản theo mẫu",
                p: "Sinh dự thảo kết luận, biên bản theo bố cục hành chính chuẩn và xuất ra Word/PDF.",
              },
            ].map((c, i) => (
              <Reveal key={i} className="card" delay={i * 70}>
                <div className={`ic ${c.cls}`}>{c.ic}</div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── How it works ───────────────────────── */}
      <section className="section soft">
        <div className="container">
          <SectionHead
            center
            eyebrow="Quy trình"
            title="Sáu bước, từ lời nói đến nhiệm vụ"
            sub="Một mạch công việc liền lạc — bạn chỉ cần ghi âm hoặc tải file, phần còn lại để hệ thống lo."
          />
          <div className="steps">
            {[
              ["Đưa âm thanh vào", "Ghi âm trực tiếp trên trình duyệt hoặc tải lên file MP3/WAV/M4A có sẵn."],
              ["Phiên âm tự động", "Hệ thống tách lời nói thành câu, gắn mốc thời gian và phân biệt người nói."],
              ["Rà soát & gán tên", "Sửa nhanh nội dung, đặt tên người nói, tách đoạn khi cần."],
              ["Phân tích AI", "Tóm tắt ý chính, nhận diện quyết định và nhiệm vụ được giao."],
              ["Sinh biên bản", "Tạo dự thảo kết luận, biên bản theo mẫu — sẵn sàng để chỉnh sửa."],
              ["Xuất & lưu trữ", "Tải Word/PDF, lưu bản ghi và theo dõi nhiệm vụ sau cuộc họp."],
            ].map(([h, p], i) => (
              <Reveal key={i} className="step" delay={(i % 3) * 70}>
                <div className="num">{String(i + 1).padStart(2, "0")}</div>
                <h3>{h}</h3>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── Split 1: transcript ───────────────────────── */}
      <section className="section">
        <div className="container split">
          <Reveal>
            <span className="eyebrow">Phiên âm theo người nói</span>
            <h2>Mỗi câu nói gắn đúng người, đúng thời điểm</h2>
            <p>
              Bản ghi văn bản được tạo theo từng mốc thời gian và phân biệt người nói. Bạn có thể
              gán tên, tách đoạn và để AI hiệu chỉnh chính tả, ngữ cảnh — bản phiên âm rõ ràng,
              dễ tra cứu.
            </p>
            <div className="ticks">
              <div className="tick">
                <span className="ck">
                  <IconCheck size={15} />
                </span>
                <span>
                  <b>Phân biệt người nói</b> — tự động tách Người nói 1, 2, 3… và cho phép đặt tên.
                </span>
              </div>
              <div className="tick">
                <span className="ck">
                  <IconCheck size={15} />
                </span>
                <span>
                  <b>Mốc thời gian</b> — nhấp vào câu để nghe lại đúng đoạn ghi âm tương ứng.
                </span>
              </div>
              <div className="tick">
                <span className="ck">
                  <IconCheck size={15} />
                </span>
                <span>
                  <b>Hiệu chỉnh bằng AI</b> — gợi ý sửa lỗi nhận diện, chuẩn hoá thuật ngữ.
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="visual">
            <div className="mock-row">
              <div className="avatar" style={{ background: "#3b4ef0" }}>
                CT
              </div>
              <div>
                <div className="who">
                  Chủ trì <span style={{ color: "#9aa6c8", fontWeight: 400 }}> · 00:12</span>
                </div>
                <div className="tx">Chúng ta bắt đầu phiên giao ban tuần, mời các đồng chí.</div>
              </div>
            </div>
            <div className="mock-row">
              <div className="avatar" style={{ background: "#14c8a8" }}>
                NV
              </div>
              <div>
                <div className="who">
                  Phòng Nghiệp vụ <span style={{ color: "#9aa6c8", fontWeight: 400 }}> · 00:48</span>
                </div>
                <div className="tx">Đã số hoá tám mươi hai phần trăm hồ sơ thủ tục.</div>
              </div>
            </div>
            <div className="mock-row">
              <div className="avatar" style={{ background: "#f0468a" }}>
                KT
              </div>
              <div>
                <div className="who">
                  Phòng Kỹ thuật <span style={{ color: "#9aa6c8", fontWeight: 400 }}> · 01:20</span>
                </div>
                <div className="tx">Cam kết xử lý dứt điểm phần đồng bộ trong hai tuần.</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────────────────── Split 2: tasks ───────────────────────── */}
      <section className="section soft">
        <div className="container split rev">
          <Reveal className="visual">
            <div className="task-row done">
              <span className="cb" />
              <span className="t">Hoàn thành đồng bộ dữ liệu một cửa</span>
              <span className="badge b2">Phòng Kỹ thuật</span>
            </div>
            <div className="task-row">
              <span className="cb" />
              <span className="t">Số hoá phần hồ sơ còn lại</span>
              <span className="badge b1">30/06</span>
            </div>
            <div className="task-row">
              <span className="cb" />
              <span className="t">Tổng hợp kết luận, gửi biên bản</span>
              <span className="badge b3">Văn phòng</span>
            </div>
            <div className="note" style={{ marginTop: 14 }}>
              AI tự bóc tách <b>nhiệm vụ — người phụ trách — thời hạn</b> trực tiếp từ nội dung
              thảo luận.
            </div>
          </Reveal>

          <Reveal delay={120}>
            <span className="eyebrow">Kết luận & giao việc</span>
            <h2>Biến quyết định trong phòng họp thành việc có người chịu trách nhiệm</h2>
            <p>
              Sau khi phân tích, hệ thống trích ra danh sách nhiệm vụ gắn với đơn vị/cá nhân và
              thời hạn hoàn thành. Dự thảo kết luận được sinh theo bố cục hành chính chuẩn: bối
              cảnh, nội dung thảo luận, kết luận của chủ trì và nhiệm vụ.
            </p>
            <div className="ticks">
              <div className="tick">
                <span className="ck">
                  <IconCheck size={15} />
                </span>
                <span>
                  <b>Nhiệm vụ có chủ</b> — không còn việc “chung chung” không ai theo dõi.
                </span>
              </div>
              <div className="tick">
                <span className="ck">
                  <IconCheck size={15} />
                </span>
                <span>
                  <b>Dự thảo sẵn sàng</b> — chỉnh sửa nhẹ rồi xuất Word/PDF để trình ký.
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────────────────── Audience ───────────────────────── */}
      <section className="section">
        <div className="container">
          <SectionHead
            center
            eyebrow="Dành cho ai"
            title="Giá trị rõ ràng cho từng vai trò"
          />
          <div className="grid cards-4">
            {[
              [<IconUsers size={22} />, "Lãnh đạo / chủ trì", "Nắm ngay ý chính, quyết định và danh sách nhiệm vụ cần theo dõi."],
              [<IconDoc size={22} />, "Thư ký / tổng hợp", "Giảm mạnh thời gian nghe lại, ghi chép và soạn kết luận sau họp."],
              [<IconTask size={22} />, "Đơn vị thực hiện", "Nhận nhiệm vụ rõ ràng theo công việc, người phụ trách và thời hạn."],
              [<IconShield size={22} />, "Quản trị / nội chính", "Dữ liệu lưu tập trung, có nhật ký thao tác, thuận tiện tra cứu."],
            ].map(([ic, h, p], i) => (
              <Reveal key={i} className="card" delay={i * 70}>
                <div className="ic">{ic as React.ReactNode}</div>
                <h3>{h as string}</h3>
                <p>{p as string}</p>
              </Reveal>
            ))}
          </div>
          <Reveal style={{ textAlign: "center", marginTop: 34 }}>
            <Link to="/giai-phap" className="btn btn-ghost btn-lg">
              Xem giải pháp theo ngành <IconArrow size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ───────────────────────── Security teaser ───────────────────────── */}
      <section className="section soft">
        <div className="container split">
          <Reveal>
            <span className="eyebrow">An toàn & kiểm soát</span>
            <h2>Dữ liệu họp là tài sản — được bảo vệ tương xứng</h2>
            <p>
              Phân quyền theo vai trò, nhật ký kiểm toán đầy đủ và khả năng triển khai trong hạ
              tầng nội bộ. Khoá tích hợp và dịch vụ bên thứ ba được xử lý hoàn toàn ở phía máy
              chủ, không lộ ra trình duyệt người dùng.
            </p>
            <div className="kicker-grid">
              <div className="mini">
                <span className="ic">
                  <IconFingerprint size={20} />
                </span>
                <div>
                  <b>Phân quyền</b>
                  <span>Người dùng thường chỉ thấy cuộc họp của mình</span>
                </div>
              </div>
              <div className="mini">
                <span className="ic teal">
                  <IconSearch size={20} />
                </span>
                <div>
                  <b>Nhật ký kiểm toán</b>
                  <span>Ghi nhận xem, sửa, tải, xoá dữ liệu</span>
                </div>
              </div>
              <div className="mini">
                <span className="ic violet">
                  <IconBolt size={20} />
                </span>
                <div>
                  <b>Triển khai nội bộ</b>
                  <span>Chạy trong hạ tầng của đơn vị</span>
                </div>
              </div>
            </div>
            <Reveal style={{ marginTop: 26 }}>
              <Link to="/bao-mat" className="btn btn-ghost">
                Tìm hiểu về bảo mật <IconArrow size={17} />
              </Link>
            </Reveal>
          </Reveal>

          <Reveal delay={120} className="visual">
            <div className="mock-row" style={{ alignItems: "center" }}>
              <div className="avatar" style={{ background: "#3b4ef0" }}>
                <IconShield size={18} />
              </div>
              <div>
                <div className="who">Quyền truy cập</div>
                <div className="tx">Vai trò: Quản trị · Phạm vi: Toàn đơn vị</div>
              </div>
              <span className="badge b2" style={{ marginLeft: "auto" }}>
                Đang bật
              </span>
            </div>
            <div className="mock-row" style={{ alignItems: "center" }}>
              <div className="avatar" style={{ background: "#14c8a8" }}>
                <IconClock size={18} />
              </div>
              <div>
                <div className="who">Nhật ký gần nhất</div>
                <div className="tx">Tải biên bản “Giao ban tuần 23” · 09:14</div>
              </div>
            </div>
            <div className="mock-row" style={{ alignItems: "center" }}>
              <div className="avatar" style={{ background: "#6a3df0" }}>
                <IconUpload size={18} />
              </div>
              <div>
                <div className="who">Lưu trữ</div>
                <div className="tx">Bản ghi mã hoá, lưu trong hạ tầng nội bộ</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
